<template>
    <v-dialog v-model="show" fullscreen scrollable transition="slide-y-transition" overlay-color="#111"
        overlay-opacity="0.95" :style="`${isAndroidNative ? 'margin-top: 100px; height: 100vh;' : ''} `">
        <v-card class="bg-dark pa-4 hardware-dialog">
            <v-btn variant="tonal" class="mt-6" @click="close"
                style="position: absolute; top: 12px; right: 12px; z-index: 10; transition: 0.3s ease;color: #fff;">

                {{ $t('closeButton') }}
            </v-btn>
            <div class="content-scroll">
                <h2 class="text-white mb-6 text-center font-weight-bold">
                    {{ $t('hardware.title') }}
                </h2>

                <div class="d-flex flex-column align-center text-center" style="gap: 8px">
                    <v-icon size="36" color="white">mdi-account</v-icon>
                    <span class="text-white font-weight-bold">{{ $t('hardware.user') }}</span>
                    <v-icon class="glow-arrow" size="28">mdi-arrow-down</v-icon>

                    <v-card class="connection-card">
                        <v-icon size="28" color="cyan">mdi-cellphone-link</v-icon>
                        <span class="mt-1 text-white">{{ $t('hardware.app') }}</span>
                    </v-card>

                    <v-icon class="glow-arrow" size="28">mdi-arrow-down</v-icon>

                    <v-card class="connection-card">
                        <v-icon size="28" color="cyan">mdi-server</v-icon>
                        <span class="mt-1 text-white">{{ $t('hardware.hub') }}</span>
                        <small class="text-grey-lighten-1">
                            {{ $t('hardware.hubSubtitle') }}
                        </small>
                    </v-card>

                    <v-icon class="glow-arrow" size="28">mdi-arrow-down</v-icon>
                </div>

                <v-card class="system-wrapper mt-6">
                    <h3 class="text-white text-center font-weight-bold">
                        {{ $t('hardware.sectionTitle') }}
                    </h3>
                    <v-row class="mt-4" justify="center" no-gutters>
                        <v-col cols="12" sm="6" md="4" class="pa-2" v-for="(hw) in $tm('hardware.devices')"
                            :key="hw.title">
                            <v-card class="system-card">
                                <v-icon :color="hw.color" size="32">{{ hw.icon }}</v-icon>
                                <div class="text-white font-weight-bold mt-2">{{ hw.title }}</div>
                                <ul class="text-white mt-2 bullet-list">
                                    <li v-for="point in hw.points" :key="point">{{ point }}</li>
                                </ul>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-card>
            </div>
        </v-card>
    </v-dialog>
</template>


<script setup>
import { ref, defineExpose } from "vue";
import { Capacitor } from '@capacitor/core';
const isAndroidNative = Capacitor.getPlatform() === 'android' && Capacitor.isNativePlatform();

const show = ref(false);
function open() {
    show.value = true;
}
function close() {
    show.value = false;
}
defineExpose({ open });

const hardwareList = [
    {
        title: "Lights",
        icon: "mdi-lightbulb-on",
        color: "amber",
        points: ["Zone-based brightness control", "Daylight-based automation"],
    },
    {
        title: "Heating",
        icon: "mdi-fire",
        color: "orange",
        points: ["Real-time temperature control", "Smart energy-saving schedules"],
    },
    {
        title: "Ventilation",
        icon: "mdi-air-filter",
        color: "blue lighten-2",
        points: ["CO₂ & humidity-based activation", "Personalized airflow zones"],
    },
    {
        title: "Blinds",
        icon: "mdi-blinds",
        color: "indigo lighten-2",
        points: ["Auto shading with sun-tracking", "Privacy & glare control"],
    },
    {
        title: "Access Control",
        icon: "mdi-lock-smart",
        color: "green lighten-1",
        points: ["NFC, Bluetooth & QR code support", "Granular role-based access"],
    },
];
</script>

<style scoped>
.bg-dark {
    background-color: #111;
    min-height: 100vh;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.hardware-dialog {
    max-width: 100vw;
    width: 100%;
    margin: 0 auto;
}

.content-scroll {
    width: 100%;
    max-width: 1200px;
    padding: 16px;
    padding-top: 64px;
    box-sizing: border-box;
}

.close-btn {
    position: fixed;
    top: 12px;
    right: 12px;
    z-index: 100;
    background-color: #000000cc;
}

.connection-card {
    background: #222;
    color: white;
    border-radius: 16px;
    padding: 16px;
    box-shadow: 0 0 20px rgba(0, 255, 255, 0.15);
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 220px;
    text-align: center;
    margin-bottom: 12px;
}

.glow-arrow {
    color: cyan;
    filter: drop-shadow(0 0 6px cyan);
}

.system-wrapper {
    background: #1a1a1a;
    border-radius: 20px;
    padding: 24px;
    width: 100%;
    max-width: 1100px;
    box-shadow: 0 0 30px rgba(0, 255, 255, 0.07);
}

.system-card {
    background-color: #2a2a2a;
    color: white;
    padding: 16px;
    border-radius: 16px;
    box-shadow: 0 4px 12px rgba(0, 255, 255, 0.05);
    text-align: center;
    height: 100%;
}

.bullet-list {
    text-align: left;
    font-size: 0.9rem;
    margin-top: 8px;
    padding-left: 16px;
}

/* Ultra-thin elegant scrollbar for WebKit (Chrome, Safari) */
::-webkit-scrollbar {
    width: 4px;
}

::-webkit-scrollbar-track {
    background: transparent;
}

::-webkit-scrollbar-thumb {
    background: rgba(0, 255, 255, 0.15);
    border-radius: 8px;
}

::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 255, 255, 0.3);
}

/* Elegant thin scrollbar for Firefox */
* {
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 255, 255, 0.15) transparent;
}
</style>